import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _extends from "@babel/runtime/helpers/extends";
import _isEmpty from "lodash/isEmpty";
var _excluded = ["ref"];
import "core-js/modules/es6.array.map.js";

/**
 * Base class is not to be exposed outside of the package
 */
import React from 'react';
import PropTypes from 'prop-types';
import Autosuggest from 'react-autosuggest';
import analyticsLib from '@nerdwallet/analytics';
import { Impression } from '@nerdwallet/analytics/react/impression';
import { Input } from '@nerdwallet/currency'; // @ts-ignore - no types available

import { ElementImpressionInteraction } from '@nerdwallet/nw-client-lib/tracking';
import { Tracking } from '@nerdwallet/nw-client-lib';
import { ENTITY_NAME, EVENT_TYPE, FORM_NAME, getLegacySearchName, SECTION_NAME } from './lib/analytics-constants';
import theme from "./marketplace-search.module.css";

var MarketplaceSearch = function MarketplaceSearch(_ref) {
  var analytics = _ref.analytics,
      error = _ref.error,
      errorMessage = _ref.errorMessage,
      id = _ref.id,
      label = _ref.label,
      onSelectHandler = _ref.onSelectHandler,
      placeholder = _ref.placeholder,
      productType = _ref.productType,
      refetch = _ref.refetch,
      renderSuggestion = _ref.renderSuggestion,
      setValue = _ref.setValue,
      suggestions = _ref.suggestions,
      queryVariables = _ref.queryVariables,
      value = _ref.value;

  // this populates the input when suggestion is clicked
  var getSuggestionValue = function getSuggestionValue(suggestion) {
    return suggestion.displayName;
  };
  /**
   * Required by <AutoSuggest>
   * Takes in this function which will be called every time you need to recalculate suggestions.
   *
   * In our case we need to recall `refetch` from useQuery to fetch for new suggestions.
   */


  var onSuggestionsFetchRequested = function onSuggestionsFetchRequested() {
    if (!_isEmpty(value)) {
      refetch(queryVariables);
    }
  };
  /**
   * Optional by <AutoSuggest>
   * Used to customize the rendering of the suggestions container.
   *
   * The usage is to pass `containerProps` to the highest level parent to preserve
   * the autosuggest's functionality.
   *
   * Here we override the default container so we can wrap the children (suggestions) in an <Impression/>
   * so when the suggestion container comes into view we fire off an an impression event.
   */


  var renderSuggestionsContainer = function renderSuggestionsContainer(_ref2) {
    var containerProps = _ref2.containerProps,
        children = _ref2.children;
    var numOfSuggestions = suggestions.length;
    var suggestionContainer = children; // we don't want the impression to fire on render when there are no results

    if (numOfSuggestions > 0) {
      suggestionContainer = /*#__PURE__*/React.createElement(Impression, {
        entityName: (analytics === null || analytics === void 0 ? void 0 : analytics.entityName) || ENTITY_NAME.MARKETPLACE_SEARCH,
        eventType: EVENT_TYPE.MARKETPLACE_OFFERS_IMPRESSION,
        id: "".concat(id, "__suggestion-container__impression"),
        payload: {
          section_name: (analytics === null || analytics === void 0 ? void 0 : analytics.sectionName) || SECTION_NAME.MARKETPLACE_SEARCH_SUGGESTIONS,
          section_position: (analytics === null || analytics === void 0 ? void 0 : analytics.sectionPosition) || 0,
          additional_attributes: {
            marketplace_product_type: productType,
            marketplace_offers_count: suggestions.length,
            marketplace_offer_ids: suggestions.map(function (result) {
              return result.id;
            })
          }
        }
      }, children);
    }

    return /*#__PURE__*/React.createElement("div", _extends({}, containerProps, {
      "aria-label": "search"
    }), suggestionContainer);
  };
  /**
   * onChange is passed to the input through InputProps
   *
   * @param event
   * @param onChangeProps
   */


  var onChange = function onChange(event, _ref3) {
    var newValue = _ref3.newValue;

    if (newValue.length > 0) {
      Tracking.formInputChanged({
        formName: getLegacySearchName(productType),
        fieldName: getLegacySearchName(productType),
        fieldValue: newValue,
        inputType: 'TEXT'
      });
    }

    setValue(newValue);
  };
  /**
   * Optional by <AutoSuggest/>
   * Takes in this function which will be called every
   * time suggestion is selected via mouse or keyboard.
   */


  var onSuggestionSelected = React.useCallback(function (event, _ref4) {
    var suggestionIndex = _ref4.suggestionIndex;
    var selectedSuggestion = suggestions[suggestionIndex];
    var selectedId = selectedSuggestion === null || selectedSuggestion === void 0 ? void 0 : selectedSuggestion.id; // bedrock instrumentation

    analyticsLib.track(EVENT_TYPE.MARKETPLACE_SEARCH_UPDATED, {
      form_name: (analytics === null || analytics === void 0 ? void 0 : analytics.formName) || FORM_NAME.MARKETPLACE_SEARCH,
      value_s: selectedId,
      was_prefilled: false
    }); // legacy instrumentation

    Tracking.elementInteraction({
      elementName: "".concat(getLegacySearchName(productType), "-result"),
      sectionCategory: getLegacySearchName(productType),
      elementContent: selectedId,
      interactionType: 'CLICK'
    });
    onSelectHandler([selectedSuggestion]);
  }, [analytics, onSelectHandler, productType, suggestions]);
  /**
   * Required by <AutoSuggest/>
   * Pass through arbitrary props to the input. It must contain at least value and onChange.
   */

  var inputProps = {
    placeholder: placeholder,
    value: value,
    onChange: onChange
  };
  /**
   * Optional by <AutoSuggest/>
   * Used to customize the rendering of the input component.
   */

  var renderInputComponent = React.useCallback(function (_ref5) {
    var ref = _ref5.ref,
        props = _objectWithoutProperties(_ref5, _excluded);

    return /*#__PURE__*/React.createElement(Impression, {
      entityName: (analytics === null || analytics === void 0 ? void 0 : analytics.entityName) || ENTITY_NAME.MARKETPLACE_SEARCH,
      eventType: EVENT_TYPE.FORM_IMPRESSION,
      id: "".concat(id, "__suggestion-input__impression"),
      payload: {
        form_name: (analytics === null || analytics === void 0 ? void 0 : analytics.formName) || FORM_NAME.MARKETPLACE_SEARCH,
        section_name: (analytics === null || analytics === void 0 ? void 0 : analytics.sectionName) || SECTION_NAME.MARKETPLACE_SEARCH
      }
    }, /*#__PURE__*/React.createElement(ElementImpressionInteraction, {
      type: "CLICK",
      elementName: getLegacySearchName(productType),
      elementCategory: "input",
      id: getLegacySearchName(productType)
    }, /*#__PURE__*/React.createElement(Input, _extends({
      inputRef: ref,
      fullWidth: true // Let's the input span 100% of its container so it can be controlled by the client see @nerdwallet/react-input for details. Defaults to a small width of 220px.
      ,
      errorMessage: errorMessage,
      error: error,
      placeholder: placeholder,
      label: label
    }, props))));
  }, [analytics, error, errorMessage, id, label, placeholder, productType]);
  return /*#__PURE__*/React.createElement(Autosuggest, {
    getSuggestionValue: getSuggestionValue,
    id: id,
    inputProps: inputProps,
    onSuggestionsClearRequested: function onSuggestionsClearRequested() {
      setValue('');
    },
    onSuggestionsFetchRequested: onSuggestionsFetchRequested,
    onSuggestionSelected: onSuggestionSelected,
    renderInputComponent: renderInputComponent,
    renderSuggestion: renderSuggestion,
    renderSuggestionsContainer: renderSuggestionsContainer,
    suggestions: suggestions || [],
    theme: theme
  });
};

MarketplaceSearch.displayName = 'MarketplaceSearch';
MarketplaceSearch.propTypes = {
  /**
   * Analytics Properties
   */
  analytics: PropTypes.shape({
    entityName: PropTypes.string,
    formName: PropTypes.string,
    sectionName: PropTypes.string,
    sectionPosition: PropTypes.number
  }),

  /**
   *
   * A boolean indicating is there is an error
   */
  error: PropTypes.bool,

  /**
   * Error message to be displayed on error.
   */
  errorMessage: PropTypes.string.isRequired,

  /**
   * Unique identifier for the component if there are multiple searches
   */
  id: PropTypes.string.isRequired,

  /**
   * Search component label
   */
  label: PropTypes.string || PropTypes.node,

  /**
   * Function to manually trigger a refetch
   * ie. when inputs change this will be called
   * Ideally comes from useQuery hook
   */
  refetch: PropTypes.func.isRequired,

  /**
   * A function that renders a component used to
   * display/format each suggestion
   */
  renderSuggestion: PropTypes.func,

  /**
   * Function to update search input changes.
   */
  setValue: PropTypes.func.isRequired,

  /**
   * The list of suggestions/search results.
   */
  suggestions: PropTypes.array,
  // eslint-disable-line react/forbid-prop-types

  /**
   * A onclick handler that gives access to the selected suggestion.
   * The selected suggestion will be wrapped in an array to give flexibility for multi-select in the future.
   */
  onSelectHandler: PropTypes.func.isRequired,

  /**
   * The text displayed when no input is entered in the input.
   */
  placeholder: PropTypes.string,

  /**
   * Product Type; e.g. credit_cards
   * Only to be used for analytics tracking purposes.
   * We want to abstract away from having the base component have knowledge of product type.
   * Since product information should be contained with each product search component.
   */
  productType: PropTypes.string,

  /**
   * The variables to be used in the refetch()
   */
  queryVariables: PropTypes.shape({}).isRequired,

  /**
   * The current input value.
   */
  value: PropTypes.string
};
MarketplaceSearch.defaultProps = {
  analytics: {},
  error: false,
  label: '',
  placeholder: '',
  suggestions: []
};
export default MarketplaceSearch;